import React, { useState } from 'react';
import { Form, Button, Card, Alert, Row, Col, Spinner, Accordion } from 'react-bootstrap';
import { FaBuilding, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaFileAlt, FaCheckCircle, FaKey } from 'react-icons/fa';

const LICENSE_OPTIONS = [
  { value: 'TRIAL', label: 'Trial', days: 30, description: '30 Days - Free Trial' },
  { value: 'STANDARD', label: 'Standard', days: 90, description: '90 Days - Quarterly' },
  { value: 'HALFYEAR', label: 'Half Year', days: 180, description: '180 Days - 6 Months' },
  { value: 'YEARLY', label: 'Full Year', days: 365, description: '365 Days - Annual' }
];

const SignupPage = () => {
  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    ownerName: '',
    companySlogan: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Software License
    licenseType: 'TRIAL',
    
    // Contractor Licenses (Optional)
    licenseNumber1: '',
    licenseNumber2: '',
    licenseNumber3: '',
    
    // Document Profile Override (Optional)
    documentPhone: '',
    documentEmail: '',
    documentAddress: '',
    documentCity: '',
    documentState: '',
    documentZipCode: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const formatPhone = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };
  
  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, [e.target.name]: formatted });
  };
  
  const getSelectedLicense = () => {
    return LICENSE_OPTIONS.find(opt => opt.value === formData.licenseType);
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      const response = await fetch('/api/onboarding/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.msg || data.errors?.[0]?.msg || 'Signup failed');
      }
      
      setSuccess(true);
    } catch (err) {
      console.error('Signup error:', err);
      setError(err.message || 'An error occurred during signup. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Success screen
  if (success) {
    return (
      <div className="container py-5">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-lg border-0">
              <Card.Body className="text-center p-5">
                <div className="mb-4">
                  <FaCheckCircle size={80} className="text-success" />
                </div>
                <h2 className="text-success mb-3">Thank You!</h2>
                <p className="lead mb-4">
                  Your signup request has been submitted successfully.
                </p>
                <div className="bg-light p-4 rounded mb-4">
                  <h5>What happens next?</h5>
                  <ol className="text-start mb-0">
                    <li className="mb-2">Our team will review your information</li>
                    <li className="mb-2">Once approved, you'll receive an email with your login credentials</li>
                    <li>You can then log in and start using the system!</li>
                  </ol>
                </div>
                <p className="text-muted">
                  This usually takes less than 24 hours during business days.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
  
  return (
    <div className="container py-5">
      <Row className="justify-content-center">
        <Col lg={8}>
          <div className="text-center mb-4">
            <h1 className="display-5 fw-bold text-primary">
              <FaBuilding className="me-3" />
              Get Started Today
            </h1>
            <p className="lead text-muted">
              Sign up for the Construction Management System and streamline your business.
            </p>
          </div>
          
          <Card className="shadow-lg border-0">
            <Card.Body className="p-4 p-md-5">
              {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
              
              <Form onSubmit={onSubmit}>
                {/* Company Information */}
                <div className="mb-4">
                  <h4 className="border-bottom pb-2 mb-3">
                    <FaBuilding className="me-2 text-primary" />
                    Company Information
                  </h4>
                  
                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>Company Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={onChange}
                          required
                          placeholder="e.g., ABC Construction LLC"
                          size="lg"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Owner Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="ownerName"
                          value={formData.ownerName}
                          onChange={onChange}
                          required
                          placeholder="e.g., John Smith"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Company Slogan (Optional)</Form.Label>
                    <Form.Control
                      type="text"
                      name="companySlogan"
                      value={formData.companySlogan}
                      onChange={onChange}
                      placeholder="e.g., Quality Work, Every Time"
                    />
                    <Form.Text className="text-muted">
                      This will appear on your proposals and invoices
                    </Form.Text>
                  </Form.Group>
                  
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaEnvelope className="me-1" /> Email *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={onChange}
                          required
                          placeholder="you@company.com"
                        />
                        <Form.Text className="text-muted">
                          We'll send your login credentials here
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label><FaPhone className="me-1" /> Phone *</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          required
                          placeholder="(555) 555-5555"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
                
                {/* Address */}
                <div className="mb-4">
                  <h4 className="border-bottom pb-2 mb-3">
                    <FaMapMarkerAlt className="me-2 text-primary" />
                    Business Address
                  </h4>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Address *</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={onChange}
                      required
                      placeholder="123 Main Street"
                    />
                  </Form.Group>
                  
                  <Row>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>City *</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={onChange}
                          required
                          placeholder="City"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>State *</Form.Label>
                        <Form.Control
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={onChange}
                          required
                          placeholder="State"
                          maxLength={2}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>Zip Code *</Form.Label>
                        <Form.Control
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={onChange}
                          required
                          placeholder="Zip"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
                
                {/* Software License */}
                <Card className="mb-4 border-primary">
                  <Card.Header className="bg-primary text-white">
                    <FaKey className="me-2" />
                    Software License
                  </Card.Header>
                  <Card.Body>
                    <Form.Group className="mb-3">
                      <Form.Label>License Type *</Form.Label>
                      <Form.Select
                        name="licenseType"
                        value={formData.licenseType}
                        onChange={onChange}
                        required
                        size="lg"
                      >
                        {LICENSE_OPTIONS.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label} - {option.days} Days
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                    
                    <div className="bg-light p-3 rounded">
                      <Row>
                        <Col>
                          <strong>Selected:</strong>{' '}
                          <span className={`badge bg-${
                            formData.licenseType === 'TRIAL' ? 'info' :
                            formData.licenseType === 'STANDARD' ? 'success' :
                            formData.licenseType === 'HALFYEAR' ? 'primary' : 'warning'
                          }`}>
                            {getSelectedLicense()?.label}
                          </span>
                        </Col>
                        <Col>
                          <strong>Duration:</strong> {getSelectedLicense()?.days} days
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
                
                {/* Optional Sections - Accordion */}
                <Accordion className="mb-4">
                  {/* Contractor License Numbers */}
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <FaFileAlt className="me-2" />
                      <span className="fw-bold">Contractor License Numbers (Optional)</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <p className="text-muted small mb-3">
                        Add contractor license numbers that will appear on proposals and invoices.
                      </p>
                      <Row>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>License #1</Form.Label>
                            <Form.Control
                              type="text"
                              name="licenseNumber1"
                              value={formData.licenseNumber1}
                              onChange={onChange}
                              placeholder="e.g., HIC-123456"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>License #2</Form.Label>
                            <Form.Control
                              type="text"
                              name="licenseNumber2"
                              value={formData.licenseNumber2}
                              onChange={onChange}
                              placeholder="e.g., EPA-789012"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>License #3</Form.Label>
                            <Form.Control
                              type="text"
                              name="licenseNumber3"
                              value={formData.licenseNumber3}
                              onChange={onChange}
                              placeholder="e.g., INS-345678"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                  
                  {/* Document Profile Override */}
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>
                      <FaFileAlt className="me-2" />
                      <span className="fw-bold">Document Profile Override (Optional)</span>
                    </Accordion.Header>
                    <Accordion.Body>
                      <p className="text-muted small mb-3">
                        If your company uses different contact info on proposals/invoices than your main contact info, enter it here. Leave blank to use the main company info above.
                      </p>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Document Phone</Form.Label>
                            <Form.Control
                              type="tel"
                              name="documentPhone"
                              value={formData.documentPhone}
                              onChange={handlePhoneChange}
                              placeholder="Leave blank to use main phone"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Document Email</Form.Label>
                            <Form.Control
                              type="email"
                              name="documentEmail"
                              value={formData.documentEmail}
                              onChange={onChange}
                              placeholder="Leave blank to use main email"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Form.Group className="mb-3">
                        <Form.Label>Document Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="documentAddress"
                          value={formData.documentAddress}
                          onChange={onChange}
                          placeholder="Leave blank to use main address"
                        />
                      </Form.Group>
                      <Row>
                        <Col md={5}>
                          <Form.Group className="mb-3">
                            <Form.Label>City</Form.Label>
                            <Form.Control
                              type="text"
                              name="documentCity"
                              value={formData.documentCity}
                              onChange={onChange}
                              placeholder="City"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4}>
                          <Form.Group className="mb-3">
                            <Form.Label>State</Form.Label>
                            <Form.Control
                              type="text"
                              name="documentState"
                              value={formData.documentState}
                              onChange={onChange}
                              placeholder="State"
                              maxLength={2}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>Zip Code</Form.Label>
                            <Form.Control
                              type="text"
                              name="documentZipCode"
                              value={formData.documentZipCode}
                              onChange={onChange}
                              placeholder="Zip"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
                
                {/* Admin User Info Note */}
                <Alert variant="info" className="mb-4">
                  <FaUser className="me-2" />
                  <strong>Admin Account:</strong> Your login credentials (username and password) will be automatically generated and emailed to you upon approval.
                </Alert>
                
                {/* Submit */}
                <div className="d-grid gap-2">
                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Submitting...
                      </>
                    ) : (
                      'Submit Signup Request'
                    )}
                  </Button>
                </div>
                
                <p className="text-center text-muted mt-3 small">
                  By signing up, you agree to our Terms of Service and Privacy Policy.
                </p>
              </Form>
            </Card.Body>
          </Card>
          
          <div className="text-center mt-4">
            <p className="text-muted">
              Already have an account? <a href="/login">Log in here</a>
            </p>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default SignupPage;
